/**
 * V3 API hook — Sprint 2 F5.
 *
 * Thin axios wrapper with JWT injection + 401 auto-logout.
 * Centralizes all V3 API calls so we can swap baseURL, add interceptors,
 * handle refresh tokens, etc. in one place.
 */
import axios from 'axios';
import config from '../../../config';
import { handleSessionExpired } from './sessionExpired';
import { refreshAccessToken, isAuthorizationDenied } from './tokenRefresh';

const api = axios.create({
    baseURL: config.hostname,
    headers: { 'Content-Type': 'application/json' },
});

// Inject token + active-concessionaire on every request.
// The active-concessionaire header is consumed by API-EGoalV3 validateToken when
// the caller's JWT carries no concessionaire (Backend User / sadmin / Super Admin
// after they pick a mall on BackendHomeView). Skipped for empty / sentinel values
// so we never send `X-Active-Concessionaire-Id: undefined`.
const isUsefulId = (v) => {
    if (!v) return false;
    const s = String(v).trim();
    return s.length > 0 && s !== 'undefined' && s !== 'null';
};

api.interceptors.request.use((req) => {
    const token = localStorage.getItem('sessiontoken');
    if (token) req.headers.Authorization = token;
    const activeCid = localStorage.getItem('concessionaireId');
    if (isUsefulId(activeCid)) req.headers['X-Active-Concessionaire-Id'] = activeCid;
    return req;
});

// Handle 401/403 — try a single shared refresh, then trigger session expiry.
// The refresh + permission-denial helpers are shared with the V2 global
// interceptor (tokenRefresh.js) so both paths behave identically.
api.interceptors.response.use(
    (res) => res,
    async (err) => {
        const original = err.config || {};
        const status = err.response && err.response.status;

        // rbac authorization denial — deny the action, keep the session.
        if (isAuthorizationDenied(err)) {
            return Promise.reject(err);
        }

        const isAuthErr = status === 401 || status === 403;
        if (!isAuthErr || original._retry) {
            return Promise.reject(err);
        }

        original._retry = true;
        const newToken = await refreshAccessToken();
        if (newToken) {
            original.headers = original.headers || {};
            original.headers.Authorization = newToken;
            return api(original);
        }

        // No usable token — session has expired (refresh missing, refresh
        // call failed, or refresh response had no token). Surface to user
        // and redirect; helper is idempotent across concurrent failures.
        handleSessionExpired();
        return Promise.reject(err);
    }
);

// Named V3 API surface
export const v3Api = {
    widgets: {
        catalog: () => api.get('/widgets/catalog').then(r => r.data),
        data: (widget_id, filters, opts = {}) => api.post(
            '/widgets/data',
            { widget_id, filters },
            opts.signal ? { signal: opts.signal } : undefined
        ).then(r => r.data),
        batch: (requests) => api.post('/widgets/data/batch', { requests }).then(r => r.data),
    },
    property: {
        listByConcessionaire: (concessionaire_id, property_id) =>
            api.post('/property/getpropertybyconcessionaire', {
                property_concessionaire_id: concessionaire_id,
                property_id: property_id || undefined,
            }).then(r => r.data),
    },
    userDashboard: {
        list: () => api.get('/userdashboard').then(r => r.data),
        get: (layoutKey = 'default') => api.get(`/userdashboard/${layoutKey}`).then(r => r.data),
        save: (payload) => api.post('/userdashboard/save', payload).then(r => r.data),
        remove: (layoutKey) => api.delete(`/userdashboard/${layoutKey}`).then(r => r.data),
    },
    spatial: {
        brief: (payload = {}) => api.post('/spatial/brief', payload).then(r => r.data),
    },
    chatbot: {
        query: (question, sessionId, propertyId, opts = {}) => api.post(
            '/chatbot/query',
            { question, session_id: sessionId, property_id: propertyId },
            { timeout: 15000, ...(opts.signal ? { signal: opts.signal } : {}) }
        ).then(r => r.data),
        history: () => api.get('/chatbot/history').then(r => r.data),
        session: (id) => api.get(`/chatbot/session/${id}`).then(r => r.data),
        // Visibility probe — { data: { enabled, reason } }. Drives the launcher
        // show/hide. Ungated on the backend (reports the org-level gate verdict).
        access: () => api.get('/chatbot/access').then(r => r.data),
    },
    ai: {
        morningBrief: (payload) => api.post('/ai/morning-brief', payload).then(r => r.data),
        insights: (payload) => api.post('/ai/insights', payload).then(r => r.data),
        alerts: (payload) => api.post('/ai/alerts', payload).then(r => r.data),
        acknowledge: (alert_id) => api.post('/ai/alerts/acknowledge', { alert_id }).then(r => r.data),
    },
    comparison: {
        compare: (payload) => api.post('/comparison/compare', payload).then(r => r.data),
        summary: (payload) => api.post('/comparison/summary', payload).then(r => r.data),
        search: (type, q, limit = 20) => api.get('/comparison/search', {
            params: { type, q, limit },
        }).then(r => r.data),
    },
    seasonal: {
        list:       (filters = {})    => api.post('/seasonal/list', filters).then(r => r.data),
        save:       (payload)         => api.post('/seasonal/save', payload).then(r => r.data),
        compare:    (payload)         => api.post('/seasonal/compare', payload).then(r => r.data),
        remove:     (eventKey)        => api.delete(`/seasonal/${eventKey}`).then(r => r.data),
        categories: ()                => api.get('/seasonal/categories').then(r => r.data),
    },
    zone: {
        list: (propertyId) => api.get('/zone/list', { params: { propertyId } }).then(r => r.data),
    },
    command: {
        get: (concessionaireId, params) =>
            api.get(`/command/${concessionaireId}`, { params }).then(r => r.data),
    },
    twin: {
        property: (propertyId) =>
            api.get(`/twin/${propertyId}`).then(r => r.data),
        unit: (propertyId, unitId) =>
            api.get(`/twin/${propertyId}/unit/${encodeURIComponent(unitId)}`).then(r => r.data),
        // Admin: upload floor-plan image/PDF. width/height are the asset's
        // intrinsic pixel dims captured client-side. FormData + Content-Type
        // override so the browser sets the multipart boundary (instance default
        // is application/json).
        uploadFloorPlan: (propertyId, file, { width, height } = {}) => {
            const fd = new FormData();
            fd.append('file', file);
            if (width != null) fd.append('width', String(width));
            if (height != null) fd.append('height', String(height));
            return api.post(`/twin/${propertyId}/floorplan`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            }).then(r => r.data);
        },
        // Admin: authoritative bulk save of unit polygons (normalized 0..1).
        saveUnits: (propertyId, units) =>
            api.put(`/twin/${propertyId}/units`, { units }).then(r => r.data),
        deleteUnit: (propertyId, unitId) =>
            api.delete(`/twin/${propertyId}/unit/${encodeURIComponent(unitId)}`).then(r => r.data),
    },
    anomalies: {
        list:        (params)              => api.get('/anomalies/list', { params }).then(r => r.data),
        get:         (id)                  => api.get(`/anomalies/${id}`).then(r => r.data),
        acknowledge: (id, notes)           => api.post(`/anomalies/${id}/acknowledge`, { notes }).then(r => r.data),
        resolve:     (id, resolutionNotes) => api.post(`/anomalies/${id}/resolve`, { resolution_notes: resolutionNotes }).then(r => r.data),
        reopen:      (id)                  => api.post(`/anomalies/${id}/reopen`).then(r => r.data),
    },
    analytics: {
        // Sprint 3.7: Pro Analytics page (/v3/analytics) wraps the legacy V2
        // /api/analytics/* surface so it benefits from the V3 session-expiry
        // interceptor. Payload shapes mirror what the V2 page already sends —
        // callers pass { query, dateRanges, timeRanges } / { concessionaire_id }.
        location: (payload) => api.post('/analytics/location', payload).then(r => r.data),
        single:   (payload) => api.post('/analytics/single',   payload).then(r => r.data),
        multi:    (payload) => api.post('/analytics/multi',    payload).then(r => r.data),
    },
    tenants: {
        // Sprint 3.9: TenantChip typeahead + tenant profile fetch.
        list:   (params = {}, opts = {}) => api.get('/v3/tenants/list', {
            params,
            ...(opts.signal ? { signal: opts.signal } : {}),
        }).then(r => r.data),
        detail: (id, opts = {}) => api.get(`/v3/tenants/${id}`,
            opts.signal ? { signal: opts.signal } : undefined
        ).then(r => r.data),
    },
    workPermit: {
        // Operations — Work Permit / NOC module. All endpoints are
        // concessionaire-scoped server-side (identity from the JWT, not the body),
        // so the frontend never sends concessionaire_id / user_type.
        // Tenant surface
        templates:       ()        => api.get('/workpermit/templates').then(r => r.data),
        components:      ()        => api.get('/workpermit/components').then(r => r.data),
        componentByName: (name)    => api.post('/workpermit/component-by-name', { name }).then(r => r.data),
        bookedSlots:     (date)    => api.post('/workpermit/booked-slots', { date }).then(r => r.data),
        mine:            ()        => api.get('/workpermit/mine').then(r => r.data),
        create:          (payload) => api.post('/workpermit/create', payload).then(r => r.data),
        // Admin approval surface
        approvals:       ()        => api.get('/workpermit/approvals').then(r => r.data),
        summary:         (id)      => api.get(`/workpermit/${id}/summary`).then(r => r.data),
        approve:         (id)      => api.post(`/workpermit/${id}/approve`).then(r => r.data),
        reject:          (id, reason) => api.post(`/workpermit/${id}/reject`, { rejection_reason: reason }).then(r => r.data),
        // Admin config (template) surface
        configCreate:    (payload) => api.post('/workpermit/config', payload).then(r => r.data),
        configUpdate:    (id, payload) => api.put(`/workpermit/config/${id}`, payload).then(r => r.data),
        configDelete:    (id)      => api.delete(`/workpermit/config/${id}`).then(r => r.data),
    },
    tenantDashboard: {
        // Sprint 3.9: legacy /tenantdashboard/* endpoints wrapped under v3Api so
        // the V3 Tenant Dashboard page benefits from the session-expiry interceptor.
        details:        (payload, opts = {}) => api.post('/tenantdashboard/getdetails',
            payload, opts.signal ? { signal: opts.signal } : undefined).then(r => r.data),
        totals:         (payload, opts = {}) => api.post('/tenantdashboard/gettotals',
            payload, opts.signal ? { signal: opts.signal } : undefined).then(r => r.data),
        salesComparison:(payload, opts = {}) => api.post('/tenantdashboard/getsalescomparison',
            payload, opts.signal ? { signal: opts.signal } : undefined).then(r => r.data),
        salesMoM:       (payload, opts = {}) => api.post('/tenantdashboard/getsalescomparisonmom',
            payload, opts.signal ? { signal: opts.signal } : undefined).then(r => r.data),
        growthAnalysis: (payload, opts = {}) => api.post('/tenantdashboard/getgrowthanalysis',
            payload, opts.signal ? { signal: opts.signal } : undefined).then(r => r.data),
        lifetimeTotals: (payload, opts = {}) => api.post('/tenantdashboard/getlifetimetotals',
            payload, opts.signal ? { signal: opts.signal } : undefined).then(r => r.data),
    },
};

export default api;
